<template>
  <vue-scroll
    ref="boardView"
    v-dragtoscroll="[
      'list-title',
      'list-header',
      'list-row',
      'draft-list-card',
      'grid-board-list-icon',
      'cards-count',
      'list-icon-toggler',
      'new-list-name-input',
      'create-add-button',
      'board-list-actions',
      'list-actions-icon',
      'drag-scroll-disable',
    ]"
    class="board-view-wrap"
    :class="[{ 'sidebar-expanded': sidebarExpanded }, { scrolling: scrolling }]"
    :ops="scrollOps"
    @handle-scroll="horizontalScrollHandler"
  >
    <SwimlanesView
      v-if="subGroupByEnabled"
      :main-list-ids="boardListGroupByOrder"
      :max-displayed-lists="maxDisplayedLists"
      :position="position"
      :container-width="containerWidth"
    />

    <draggable
      v-else
      v-model="listIds"
      :item-key="(listId) => listId"
      group="lists"
      class="draggable-content"
      handle=".list-header"
      filter="
        .board-list-actions,
        input.list-title,
        .draft-list-card,
        .status-picker-wrap,
        .status-icon-dropdown-wrap
        "
      :prevent-on-filter="false"
      :move="checkBoardListMove"
      :force-fallback="true"
      :fallback-tolerance="10"
      :disabled="draggingDisabled"
      :style="{
        ...(isCardsLayoutList && isAnyCardDisplayedInDockedLayout && { 'min-width': '1200px' }),
      }"
      @start="dragStart"
      @end="dragEnd"
      @change="boardListsChanged"
    >
      <template #item="{ element: listId, index }">
        <div
          :key="listId"
          v-observe-visibility="{
            callback: (isVisible, entry) => visibilityChanged(isVisible, entry, listId),
            throttle: 1000,
            throttleOptions: {
              leading: 'visible',
            },
          }"
          class="board-column"
          :data-list-id="listId"
        >
          <BoardList
            :ref="`boardList-${listId}`"
            :visible="visibleListIds.indexOf(listId) > -1"
            :list-id="listId"
            :index="index"
            :large-board="largeBoard"
            :max-displayed-lists="maxDisplayedLists"
            :expandable="isListOrTimelineLayout"
            :position="position"
            :container-width="containerWidth"
            :sprint-disable-drag="sprintDisableDrag"
            :show-add-card-button="!isCancelledOrCompleted"
            :group-by="boardGroupBy"
          />
        </div>
      </template>
      <template #footer>
        <DraftListCreate
          v-if="showDraftListCreate && !isSprint"
          :key="`dlc-${board.id}`"
          class="board-column"
          @horizontalScrollToEnd="scrollToEnd"
        />
      </template>
    </draggable>
  </vue-scroll>
</template>

<script>
import styleVariables from '@/assets/scss/variables.module.scss';
import boardViewMixin from '@/mixins/boardViewMixin';
import BoardList from '@/components/widgets/BoardList';
import DraftListCreate from '@/components/widgets/DraftListCreate';
import boardItemListMixin from '@/mixins/boardItemListMixin';
import EventBus from '@/utilities/eventBus';
import isInViewport from '@/utilities/isInViewport';
import customIconsMixin from '@/mixins/customIconsMixin';
import { groupByOption } from '@/types/groupByOption';
import useReconnectionCallbacks from '@/utilities/composables/useReconnectionCallbacks';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import { ResourceType } from '@/types/resources';
import { SprintState } from '@/types/sprint';
import useShortcutHandlers from '@/utilities/composables/useShortcutHandlers';
import { ListBehavior } from '@superthread-com/api';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import { EpicTabId } from '@/types/tabs';
import SwimlanesView from '@/components/widgets/SwimlanesView.vue';

let addCardButton;
let newListButton;

export default {
  name: 'BoardGridViewContent',
  components: {
    BoardList,
    DraftListCreate,
    SwimlanesView,
  },
  mixins: [boardItemListMixin, customIconsMixin, boardViewMixin],
  props: {
    position: {
      type: Number,
      required: false,
      default: 0,
    },
    containerWidth: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  setup() {
    const { reconnectionCallbacksIds, addReconnectionCallback, removeReconnectionCallbacks } =
      useReconnectionCallbacks();
    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
    const { copyGitBranchNameShortcutHandler } = useShortcutHandlers();
    const { isGuestReader } = useCurrentRole();

    return {
      copyGitBranchNameShortcutHandler,
      reconnectionCallbacksIds,
      addReconnectionCallback,
      removeReconnectionCallbacks,
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
      isGuestReader,
    };
  },

  data() {
    return {
      vw: window.innerWidth,
      visibleListIds: [],
      largeBoardCardsCount: 500,
      scrollingTimeout: null,
      scrolling: false,
      currentScrollPosition: 0,
      pressedAltCombination: null,
      scrollOps: {
        scrollPanel: {
          speed: 0,
        },
        rail: {
          gutterOfSide: '8px',
          gutterOfEnds: '24px',
        },
        bar: { keepShow: true },
      },
      listenersAdded: false,
      epicBoardFullyLoaded: false,
      loadingEpicsBoard: false,
      scrollYChangeTimeout: null,
      moveTolastPosition: false,
    };
  },
  computed: {
    isBoardOrEpicsBoardRoute() {
      return [
        this.$constants.epicsBoardRoute.name,
        this.$constants.routeNames.board,
        this.$constants.routeNames.sprint,
      ].includes(this.$route.name);
    },
    isAnyCardDisplayedInDockedLayout() {
      return this.$store.getters.getIsAnyCardDisplayedInLayout(this.$constants.docked);
    },
    isAnyCardDisplayed() {
      return this.$store.getters.getIsAnyCardDisplayed;
    },
    board() {
      return this.$store.getters.getFlatBoards[this.currentBoardId] || {};
    },

    isCancelledOrCompleted() {
      return [SprintState.Canceled, SprintState.Completed].includes(this.board.state);
    },

    sprintDisableDrag() {
      return this.isSprint && this.isCancelledOrCompleted;
    },

    currentBoardId() {
      if (this.embed) {
        return this.embeddedBoardAttributes?.boardId;
      }
      return this.$store.getters.currentBoardId;
    },
    sidebarExpanded() {
      return this.$store.getters.sidebarExpanded;
    },
    cardsLayout() {
      if (this.embed && !this.inEpicBoard) {
        return (
          this.embeddedBoardAttributes?.layout ||
          this.$store.getters.cardsLayout(this.currentBoardId)
        );
      }
      return this.$store.getters.cardsLayout(this.currentBoardId);
    },
    isCardsLayoutList() {
      return !!this.currentBoardId && this.cardsLayout === 'list';
    },
    isCardsLayoutTimeline() {
      return !!this.currentBoardId && this.cardsLayout === 'timeline';
    },
    isListOrTimelineLayout() {
      return this.isCardsLayoutList || this.isCardsLayoutTimeline;
    },
    maxDisplayedLists() {
      const boardPadding = parseInt(styleVariables.draggableContentHPadding, 10);
      const draggableArea = this.sidebarExpanded
        ? this.vw - parseInt(styleVariables.sidebarWidth, 10) - boardPadding
        : this.vw - parseInt(styleVariables.collapsedSidebarWidth, 10) - boardPadding;

      const columnWidth = parseInt(styleVariables.listColumnWidth, 10);

      return Math.ceil(draggableArea / columnWidth);
    },
    boardDropdownsOpened() {
      return (
        this.$store.getters.getCurrentCardIdActionEdit !== '' ||
        this.currentCardIdMemberDropdown !== ''
      );
    },
    largeBoard() {
      return this.boardCardsCount > this.largeBoardCardsCount;
    },
    boardCardsCount() {
      // note this computed will not be reactive, used only on mounted
      return this.board.lists?.reduce((prev, next) => {
        if (next == null) return prev;
        return prev + (next.total_cards || 0);
      }, 0);
    },
    timelineSidebarWidth() {
      return this.$store.getters.getTimelineSidebarWidth;
    },

    boardGroupBy() {
      if (this.embed && !this.inEpicBoard) {
        return this.embeddedBoardAttributes?.groupBy || groupByOption.status;
      }
      return this.$store.getters.getBoardGroupBy(this.currentBoardId);
    },

    groupByStatus() {
      return this.boardGroupBy === groupByOption.status;
    },

    groupByTagOrAssignee() {
      return (
        this.boardGroupBy === groupByOption.tag || this.boardGroupBy === groupByOption.assignee
      );
    },

    groupByEpic() {
      return this.boardGroupBy === groupByOption.epic;
    },

    boardListGroupBy() {
      const boardListGroupBy = this.$store.getters.getBoardGroupByObject(this.currentBoardId);
      if (boardListGroupBy) {
        return boardListGroupBy;
      } else {
        return [];
      }
    },

    boardListGroupByOrderStatus() {
      if (this.groupByStatus) {
        return this.listIds;
      }
      return null;
    },

    boardListGroupByOrderGrouped() {
      if (this.groupByStatus) {
        return null;
      }
      const groupByListOrder = { ...this.$store.getters.getGroupByListOrder(this.currentBoardId) };
      if (groupByListOrder[this.boardGroupBy]) {
        return groupByListOrder[this.boardGroupBy];
      }
      return this.boardListGroupBy.order;
    },

    boardListGroupByOrder() {
      return this.boardListGroupByOrderStatus ?? this.boardListGroupByOrderGrouped;
    },

    draggingDisabled() {
      return (
        this.isMobile ||
        this.embeddedBoardAttributes.readOnly ||
        this.isSprint ||
        this.inEpicBoard ||
        this.isGuestReader
      );
    },

    showDraftListCreate() {
      return (
        this.groupByStatus &&
        !this.hideEmptyGroups &&
        !this.embeddedBoardAttributes.readOnly &&
        !this.isGuestReader
      );
    },

    isSprint() {
      return this.$route.name === this.$constants.routeNames.sprint || this.isInboxSprint;
    },

    isInboxSprint() {
      return (
        this.$route.name === this.$constants.routeNames.inbox &&
        this.$store.getters.getCurrentInboxResource.resource_type === ResourceType.Sprint
      );
    },

    currentCardIdMemberDropdown() {
      return this.$store.getters.getCurrentCardIdMemberDropdown;
    },

    lists() {
      return this.$store.getters.lists;
    },

    listIds() {
      if (this.isSprint && this.board?.state === SprintState.Completed) {
        return this.boardListsIds.reduce((acc, listId) => {
          const list = this.lists[listId];
          if (this.checkListCompletedSprint(list?.behavior)) {
            acc.push(list.id);
          }
          return acc;
        }, []);
      }

      return this.boardListsIds;
    },

    subGroupByEnabled() {
      if (this.embed && !this.isSprint) {
        return this.embeddedBoardAttributes?.enableSubGroupBy;
      }
      return this.$store.getters.getSubGroupByEnabled(this.currentBoardId);
    },
  },

  watch: {
    currentBoardId(newVal, oldVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$refs.boardView?.scrollTo?.({
            x: '0%',
            y: '0%',
          });
          const boardTypeSprint =
            this.embed && this.embeddedBoardAttributes?.boardType === 'sprint';
          if (newVal !== oldVal && !this.isSprint && !boardTypeSprint) {
            this.onCreated(newVal);
          }
        });
      }
    },

    isListOrTimelineLayout: {
      handler(value) {
        if (!this.currentBoardId || this.subGroupByEnabled) return;
        this.$nextTick(() => {
          if (this.scrollOps?.scrollPanel) {
            this.scrollYChangeTimeout = setTimeout(() => {
              // do this after the scroll is done or clear timeout if component is destroyed
              this.scrollOps.scrollPanel.scrollingY = value;
            }, 100);
          }
        });
      },
      immediate: true,
    },
    subGroupByEnabled: {
      handler(value) {
        if (value) {
          this.$nextTick(() => {
            if (this.scrollOps?.scrollPanel) {
              this.scrollOps.scrollPanel.scrollingY = value;
            }
          });
        }
      },
      immediate: true,
    },
  },

  created() {
    this.setListsIdsWatcher();
  },

  mounted() {
    this.addShortcutEvents();
    EventBus.$on('list:checkIsInViewport', this.listViewportMessageHandler);
    EventBus.$on('boardSaveLastScrollPosition', this.saveLastScrollPosition);
    addCardButton = this.$cons.addCardButton;
    newListButton = this.$cons.newListButton;
    this.addReconnectionCallback(this.onCreated);
  },
  activated() {
    this.addShortcutEvents();
  },
  deactivated() {
    this.removeShortcutEvents();
  },
  beforeUnmount() {
    this.removeShortcutEvents();
    EventBus.$off('list:checkIsInViewport', this.listViewportMessageHandler);
    if (this.scrollingTimeout) {
      clearTimeout(this.scrollingTimeout);
    }
    if (this.scrollYChangeTimeout) {
      clearTimeout(this.scrollYChangeTimeout);
    }

    this.removeReconnectionCallbacks();
  },
  unmounted() {
    EventBus.$off('boardSaveLastScrollPosition');
    this.$store.dispatch('lastHoveredListId', '');
  },
  methods: {
    defaultHoveredCardValue() {
      if (!this.listIds.includes(this.$store.getters.lastHoveredListId)) {
        const firstList = this.listIds[0] || {};
        const [firstCardIdInFirstList] = this.groupByStatus
          ? (this.$store.getters.lists[firstList] || {}).card_order || []
          : (this.boardListGroupBy[firstList] || {}).cardIds || [];
        if (!this.inEpicBoard) {
          this.$store.dispatch('lastHoveredCardId', firstCardIdInFirstList || '');
          this.$store.dispatch('setSelectedCardId', firstCardIdInFirstList || '');
        }
        this.$store.dispatch('lastHoveredListId', firstList || '');
        this.$refs.boardView.scrollTo({
          x: '0%',
          y: '0%',
        });
      }
    },

    onResize() {
      if (this.vw !== window.innerWidth) {
        this.vw = window.innerWidth;
      }
    },
    scrollToEnd() {
      this.$refs.boardView.scrollTo({
        x: '100%',
        y: '100%',
      });
    },
    horizontalScrollHandler() {
      this.currentScrollPosition = this.$refs.boardView.getPosition().scrollLeft;
      this.setScrolling();
      if (this.boardDropdownsOpened) {
        this.hideCardEdit();
      }
    },
    setScrolling() {
      if (this.scrolling || this.isCardsLayoutList) return;
      this.scrolling = true;
      this.scrollingTimeout = setTimeout(() => {
        this.scrolling = false;
      }, 600);
    },
    saveLastScrollPosition(id, route) {
      route.meta.scrollPositions[id] = this.currentScrollPosition;
    },
    visibilityChanged(visible, entry, listId) {
      const listVisibleIndex = this.visibleListIds.indexOf(listId);
      if (visible) {
        if (listVisibleIndex === -1) {
          this.visibleListIds.push(listId);
        }
      } else if (
        listVisibleIndex !== -1 &&
        this.draggedEntity.listId !== listId &&
        this.largeBoard
      ) {
        this.visibleListIds.splice(listVisibleIndex, 1);
      }
    },
    isInViewportAndScrollVertical(selector) {
      const boardViewNode = this.$refs.boardView.$el;
      // get next card ref and element
      const nextlistElement = boardViewNode.querySelector(selector);
      if (!nextlistElement) return;

      const { vertical, deltaV } = isInViewport(nextlistElement, this.$refs.boardView);
      if (!vertical) {
        this.$refs.boardView.scrollBy({ dy: deltaV }, 0);
      }
    },
    addShortcutEvents() {
      if (this.listenersAdded) return;
      window.addEventListener('keydown', this.boardsShortcutMatchers, true);
      window.addEventListener('keyup', this.keyUpHandler, true);
      window.addEventListener('resize', this.onResize);
      this.listenersAdded = true;
    },
    removeShortcutEvents() {
      if (!this.listenersAdded) return;
      window.removeEventListener('keydown', this.boardsShortcutMatchers, true);
      window.removeEventListener('keyup', this.keyUpHandler, true);
      window.removeEventListener('resize', this.onResize);
      this.listenersAdded = false;
    },
    keyUpHandler() {
      this.pressedAltCombination = null;
    },
    shortcutAllowed() {
      const { tagName, isContentEditable } = document.activeElement;
      const [, epicId] = this.embeddedBoardAttributes?.boardId?.split('-') ?? [];
      const epicTabCards = this.$store.getters.getEpicSelectedTabId(epicId) === EpicTabId.Cards;

      return (
        this.isBoardOrEpicsBoardRoute &&
        tagName.toLowerCase() !== 'input' &&
        tagName.toLowerCase() !== 'textarea' &&
        !isContentEditable &&
        !this.$store.getters.showSearchModal &&
        (!this.isAnyCardDisplayed ||
          (this.$store.getters.getIsCardDisplayed(epicId) &&
            epicTabCards &&
            this.$store.getters.getDisplayedCards.length === 1)) &&
        (!this.inEpicBoard || (this.inEpicBoard && epicTabCards)) &&
        !this.draggingIsOn &&
        !this.embeddedBoardAttributes.disableKeyboardNavigation &&
        !this.currentCardIdMemberDropdown
      );
    },
    boardsShortcutMatchers(e) {
      if (!this.shortcutAllowed() || this.subGroupByEnabled) return;

      const { code, altKey, ctrlKey, metaKey, shiftKey } = e;
      const isModifierPressed = metaKey || shiftKey || ctrlKey;

      const handlers = {
        ...(!this.isGuestReader && {
          KeyR: this.keyRHandler,
          KeyA: this.keyAHandler,
          KeyD: this.keyDHandler,
          KeyL: this.keyTHandler,
          KeyT: this.keyTHandler,
          KeyI: this.keyIHandler,
          KeyS: this.keySHandler,
        }),
        ArrowDown: this.arrowDownHandler,
        ArrowUp: this.arrowUpHandler,
        ArrowRight: this.arrowRightHandler,
        ArrowLeft: this.arrowLeftHandler,
        Enter: this.enterHandler,
      };

      const altHandlers =
        !this.isGuestReader && !this.inEpicBoard
          ? {
              ArrowDown: this.altArrowUpDownHandler,
              ArrowUp: this.altArrowUpDownHandler,
              ArrowRight: this.altArrowRightHandler,
              ArrowLeft: this.altArrowLeftHandler,
            }
          : {};

      const ctrlShiftHandlers = {
        Period: this.periodHandler,
      };

      if (altKey && altHandlers[code]) {
        this.handleAltKey(e, code, altHandlers);
      } else if (!isModifierPressed && handlers[code]) {
        this.handleRegularKey(e, code, handlers);
      } else if ((ctrlKey || metaKey) && shiftKey && ctrlShiftHandlers[code]) {
        this.handleCtrlShiftKey(e, code, ctrlShiftHandlers);
      }
    },

    handleAltKey(e, code, handlers) {
      if (!this.pressedAltCombination) {
        this.pressedAltCombination = code;
      } else if (this.pressedAltCombination !== code) {
        return;
      }
      this.suspendMouseAndPreventEvent(e);
      handlers[code](e);
    },

    handleRegularKey(e, code, handlers) {
      this.suspendMouseAndPreventEvent(e);
      handlers[code](e);
    },

    handleCtrlShiftKey(e, code, handlers) {
      e.preventDefault();
      e.stopPropagation();
      handlers[code](e);
    },

    suspendMouseAndPreventEvent(e) {
      this.$store.dispatch('setBoardViewMouseSuspended', true);
      e.preventDefault();
      e.stopPropagation();
    },
    handleListLayoutNavigation(listIndex, listCardIds, nextCardId) {
      // Handle navigation when at the end of a list
      if (listIndex === this.listIds.length - 1) {
        if (!this.groupByStatus) return;
        nextCardId = newListButton;
        this.setLastHoveredAndSelectedCardId(nextCardId);
        this.scrollToElement('.draft-list-create-wrap');
        return;
      }

      // Handle navigation to next list
      const nextListIndex = listIndex + 1;
      const nextListId = this.listIds[nextListIndex];
      const nextListCardIds = this.getListCardsIds(nextListId);

      if (nextListId && nextListCardIds?.length > 0) {
        nextCardId = nextListCardIds[0];
        this.$store.dispatch('lastHoveredListId', nextListId);
        this.setLastHoveredAndSelectedCardId(nextCardId);
        this.scrollToCard(nextCardId, nextListId);
        return;
      }

      // Handle empty next list
      this.$store.dispatch('lastHoveredListId', nextListId);
      this.setLastHoveredAndSelectedCardId(`${addCardButton}${nextListId}`);
      this.scrollToElement(`.list-footer[data-list-id="${nextListId}"]`);
    },

    handleEndOfListNavigation(listCardIds, lastCardIndex, selectedCardId) {
      if (lastCardIndex === listCardIds.length - 1) {
        this.scrollToElement(
          `.list-footer[data-list-id="${this.$store.getters.lastHoveredListId}"]`
        );
      }

      if (
        lastCardIndex === listCardIds.length - 1 &&
        selectedCardId &&
        !selectedCardId.includes(addCardButton)
      ) {
        return `${addCardButton}${this.$store.getters.lastHoveredListId}`;
      }
      return null;
    },

    scrollToElement(selector) {
      this.isInViewportAndScrollVertical(selector);
    },

    scrollToCard(cardId, listId) {
      this.scrollToElement(`.list-row[data-card-id="${cardId}"][data-list-id="${listId}"]`);
    },

    getNextFirstListCardId(listId, right) {
      const startIndex = this.listIds.indexOf(listId);
      const direction = right ? 1 : -1;
      for (let i = startIndex; i >= 0 && i < this.listIds.length; i += direction) {
        const nextListId = this.listIds[i];
        const nextListCardIds = this.getListCardsIds(nextListId);
        if (nextListCardIds.length) {
          return { cardId: nextListCardIds[0], listId: nextListId };
        }
      }
      return null;
    },

    handleEmptyListInEpicBoard(listCardIds) {
      // if no cards in the list in epicboard, go to the first card in the next list
      if (!listCardIds.length && this.inEpicBoard) {
        const listIndex = this.listIds.indexOf(this.$store.getters.lastHoveredListId);
        const listId = this.listIds[listIndex];
        let result = this.getNextFirstListCardId(listId, true);
        if (!result?.cardId) {
          result = this.getNextFirstListCardId(listId, false);
        }
        this.$store.dispatch('lastHoveredListId', result.listId);
        this.setLastHoveredAndSelectedCardId(result.cardId);
        this.scrollToCard(result.cardId, result.listId);
        return true;
      }
      return false;
    },

    arrowDownHandler() {
      this.defaultHoveredCardValue();
      const { selectedCardId, lastHoveredListId } = this.$store.getters;
      // check is new list selected
      if (selectedCardId?.includes(newListButton)) return;
      const { listCardIds, lastCardIndex } = this.getLastCardIndex();
      if (this.handleEmptyListInEpicBoard(listCardIds)) return;
      let nextCardId = listCardIds[lastCardIndex + 1];

      // nextCardId will be undefined if the 'Add card' button is selected
      if (this.isGuestReader && !nextCardId) return;

      if (lastCardIndex === listCardIds.length - 1) {
        const selectorListFooter = `.list-footer[data-list-id="${lastHoveredListId}"]`;
        this.isInViewportAndScrollVertical(selectorListFooter);
      }
      if (this.isListOrTimelineLayout && selectedCardId && selectedCardId.includes(addCardButton)) {
        const listIndex = this.listIds.indexOf(lastHoveredListId);
        // check if list is the last one
        this.handleListLayoutNavigation(listIndex, listCardIds, nextCardId);
        return;
      }
      if (
        (lastCardIndex === listCardIds.length - 1 &&
          selectedCardId &&
          !selectedCardId.includes(addCardButton) &&
          !this.inEpicBoard) ||
        (lastCardIndex === -1 && !this.inEpicBoard)
      ) {
        // if the end of the list go to add card button
        nextCardId = `${addCardButton}${this.$store.getters.lastHoveredListId}`;
      }
      const noLastCardIndexOrAddCardButton =
        lastCardIndex !== -1 || nextCardId?.includes(addCardButton);
      const noLastCardIndexAndInEpicBoard = lastCardIndex === -1 && this.inEpicBoard;

      if (nextCardId && (noLastCardIndexOrAddCardButton || noLastCardIndexAndInEpicBoard)) {
        this.setLastHoveredAndSelectedCardId(nextCardId);
        this.scrollToCard(nextCardId, lastHoveredListId);
      }
    },
    getLastCardIndex() {
      const { lastHoveredCardId, selectedCardId } = this.$store.getters;
      const listCardIds = this.getListCardsIds();
      // get next card in the last hovered list of cards
      const lastCardIndex = listCardIds.indexOf(lastHoveredCardId || selectedCardId);

      return { listCardIds, lastCardIndex };
    },
    getListCardsIds(listId = this.$store.getters.lastHoveredListId) {
      // get the list of cards from the component because they could be filtered
      const listComponent = this.$refs[`boardList-${listId}`];

      return listComponent?.listCards ?? [];
    },
    setLastHoveredAndSelectedCardId(id) {
      this.$store.dispatch('lastHoveredCardId', id);
      this.$store.dispatch('setSelectedCardId', id);
    },
    selectInListAbove(lastHoveredListId, selectedCardId) {
      const listIndex = this.listIds.indexOf(lastHoveredListId);
      // check if first list
      if (listIndex === 0) return;
      const nextListIndex = selectedCardId.includes(newListButton) ? listIndex : listIndex - 1;
      const nextListId = this.listIds[nextListIndex];
      if (nextListId) {
        const nextCardId = `${addCardButton}${nextListId}`;
        this.$store.dispatch('lastHoveredListId', nextListId);
        this.setLastHoveredAndSelectedCardId(nextCardId);
        const selectorListFooter = `.list-footer[data-list-id="${nextListId}"]`;
        this.isInViewportAndScrollVertical(selectorListFooter);
      }
    },
    arrowUpHandler() {
      this.defaultHoveredCardValue();
      const { selectedCardId, lastHoveredListId } = this.$store.getters;
      // new list selected
      if (selectedCardId.includes(newListButton)) {
        this.selectInListAbove(lastHoveredListId, selectedCardId);
        return;
      }
      const { listCardIds, lastCardIndex } = this.getLastCardIndex();
      if (this.handleEmptyListInEpicBoard(listCardIds)) return;
      let nextCardId = listCardIds[lastCardIndex - 1];
      if (this.isListOrTimelineLayout && lastCardIndex === 0) {
        this.selectInListAbove(lastHoveredListId, selectedCardId);
        return;
      }
      if (!nextCardId) {
        if (lastCardIndex <= 0) {
          if (!listCardIds.length) {
            // if the list is empty go to add card button
            this.selectInListAbove(lastHoveredListId, selectedCardId);
            return;
          } else {
            // go to the first card in the list
            nextCardId = listCardIds[0];
          }
          if (selectedCardId.includes(addCardButton)) {
            // if at add card button go to last card
            nextCardId = listCardIds[listCardIds.length - 1];
          }
        }
      }
      if (nextCardId) {
        this.setLastHoveredAndSelectedCardId(nextCardId);
      }
      const selector = `.list-row[data-card-id="${nextCardId}"][data-list-id="${lastHoveredListId}"]`;

      this.isInViewportAndScrollVertical(selector);
    },
    arrowRightHandler(e) {
      if (this.isListOrTimelineLayout) {
        const { lastHoveredCardId, selectedCardId, lastHoveredListId } = this.$store.getters;
        EventBus.$emit('card:openChildCardsMessage', {
          cardId: selectedCardId || lastHoveredCardId,
          listId: lastHoveredListId,
        });
        return;
      }
      this.horizontalKeyHandler(e.code);
    },

    calculateNextCardPosition(
      currentCardNode,
      right,
      boardColumnPadding3x,
      nextListId,
      boardViewNode,
      boardViewRef
    ) {
      const nodeCoords = currentCardNode.getBoundingClientRect();
      const center = {
        x: nodeCoords.left + nodeCoords.width / 2,
        y: nodeCoords.top + nodeCoords.height / 2,
      };

      // Predict x coordinate of the next card
      let predictedX = right
        ? nodeCoords.right + boardColumnPadding3x
        : nodeCoords.left - boardColumnPadding3x;

      // Get next list ref and element
      const selector = `.board-column[data-list-id="${nextListId}"]`;
      const nextlistElement = boardViewNode.querySelector(selector);

      const { horizontal, deltaH } = isInViewport(nextlistElement, boardViewRef);

      if (!horizontal && deltaH) {
        // Scroll to next list
        const absDelta = Math.abs(deltaH);
        const dx = right ? absDelta : -absDelta;
        boardViewRef.scrollBy({ dx }, 0);

        // Adjust predicted X coordinate based on scrolling
        predictedX += right ? -absDelta : absDelta;
      }

      return { center, predictedX };
    },

    findNextCardForGuestReader(nextListIndex, right, boardViewNode, boardColumnPadding3x) {
      const direction = right ? 1 : -1;

      // Loop through lists to find the next card in the direction
      for (let i = nextListIndex; i >= 0 && i < this.listIds.length; i += direction) {
        const listId = this.listIds[i];
        const templistCardIds = this.getListCardsIds(listId);

        if (templistCardIds.length) {
          const nextCardId = templistCardIds[0];
          this.$store.dispatch('lastHoveredListId', listId);
          this.setLastHoveredAndSelectedCardId(nextCardId);

          const tempCurrentCardNode = boardViewNode.querySelector(
            `[data-card-id="${nextCardId}"][data-list-id="${listId}"]`
          );

          this.calculateNextCardPosition(
            tempCurrentCardNode,
            right,
            boardColumnPadding3x,
            listId,
            boardViewNode,
            this.$refs.boardView
          );

          return { nextCardId, nextListId: listId }; // Return the found values
        }
      }
      return { nextCardId: null, nextListId: null };
    },

    getCardIdToLookFor() {
      const { selectedCardId, lastHoveredCardId, lastHoveredListId } = this.$store.getters;
      let cardIdToLookFor = selectedCardId || lastHoveredCardId;
      if (this.inEpicBoard) {
        // if in epicboard and card is not in the list, move to the first card in the list
        const cardIsInBoard = this.board.lists.some((list) =>
          list.card_order.includes(cardIdToLookFor)
        );
        if (!cardIsInBoard) {
          const cardsInList = this.getListCardsIds(lastHoveredListId);
          cardIdToLookFor = cardsInList[0];
        }
      }
      return cardIdToLookFor;
    },

    findCurrentCardNode(cardIdToLookFor, lastHoveredListId, boardViewNode) {
      // find current card or button node
      let currentCardNode = boardViewNode.querySelector(
        `[data-card-id="${cardIdToLookFor}"][data-list-id="${lastHoveredListId}"]`
      );

      // if thre is no current card node check if there is a button node
      if (!currentCardNode) {
        if (this.isGuestReader) return null;
        currentCardNode = boardViewNode.querySelector(
          '.create-add-button[data-selected-card="true"]'
        );
        // if still no node use add card button of current list
        if (!currentCardNode) {
          // get list ref and element
          const listSelector = `.board-column[data-list-id="${lastHoveredListId}"]`;
          const listElement = boardViewNode.querySelector(listSelector);
          currentCardNode = listElement.querySelector('.create-add-button');
        }
      }

      return currentCardNode;
    },

    handleEmptyListCase(
      nextListId,
      nextListIndex,
      right,
      boardViewNode,
      boardColumnPadding3x,
      altKey
    ) {
      const listCardIds = this.getListCardsIds(nextListId);
      if (this.isGuestReader || this.inEpicBoard) {
        const { cardId: guestNextCardId, listId: guestNextListId } =
          this.findNextCardForGuestReader(
            nextListIndex,
            right,
            boardViewNode,
            boardColumnPadding3x
          );

        if (guestNextCardId) {
          return { cardId: guestNextCardId, listId: guestNextListId };
        }
      } else {
        // if still not found pick the last card in the list
        if (listCardIds.length) {
          if (altKey) {
            // if moving card to empty space in list, go to last position
            this.moveTolastPosition = true;
          }
          return { cardId: listCardIds[listCardIds.length - 1], listId: nextListId };
        } else {
          // if list has no cards, select the list and Add Card button
          return { cardId: `${addCardButton}${nextListId}`, listId: nextListId };
        }
      }
      return { cardId: null, listId: null };
    },

    handleHorizontalAltKeyCardMove(nextCardId, nextListId, cardIdToLookFor, selectedCardId) {
      const lastHoveredListId = this.$store.getters.lastHoveredListId;
      // get current index of card in current list
      const currentCardIndex = this.getListCardsIds(lastHoveredListId).indexOf(cardIdToLookFor);
      // get index of the card from predicted position
      const nextListCardIds = this.getListCardsIds(nextListId);
      const nextCardIndex = this.moveTolastPosition
        ? nextListCardIds.length
        : nextListCardIds.indexOf(nextCardId);

      const cardType =
        this.board.id === this.$constants.epicsBoardId ? ResourceType.Epic : ResourceType.Card;

      EventBus.$emit('card:add', {
        listId: nextListId,
        newIndex: nextCardIndex,
        oldIndex: currentCardIndex,
        cardId: cardIdToLookFor,
        boardId: this.board.id,
        projectId: this.board.project_id,
        originalListId: lastHoveredListId,
        type: cardType,
      });
      this.$nextTick(() => {
        // card can be partially out of the viewport vertically
        EventBus.$emit('card:checkIsInViewport', cardIdToLookFor);
      });
      if (selectedCardId !== cardIdToLookFor) {
        this.$store.dispatch('setSelectedCardId', cardIdToLookFor);
      }
    },

    findCardWithOffset(x, y) {
      // when next card is not found check if list has cards and try picking from the point +5px/-5px
      const cardAboveRow = document.elementFromPoint(x, y - 5)?.closest('.list-row');
      if (cardAboveRow) return cardAboveRow.dataset.cardId;

      const cardBelowRow = document.elementFromPoint(x, y + 5)?.closest('.list-row');
      if (cardBelowRow) return cardBelowRow.dataset.cardId;
      return null;
    },

    findNextHorizontalCardInList(
      nextListId,
      cardIdToLookFor,
      right,
      lastHoveredListId,
      nextListIndex,
      altKey
    ) {
      const boardViewNode = this.$refs.boardView.$el;
      const boardColumnPadding3x = parseInt(styleVariables.boardColumnPaddingLeft, 10) * 4;
      // find current card or button node
      const currentCardNode = this.findCurrentCardNode(
        cardIdToLookFor,
        lastHoveredListId,
        boardViewNode
      );
      if (!currentCardNode) return { cardId: null, listId: null };
      const { center, predictedX } = this.calculateNextCardPosition(
        currentCardNode,
        right,
        boardColumnPadding3x,
        nextListId,
        boardViewNode,
        this.$refs.boardView
      );
      const nextCard = document.elementFromPoint(predictedX, center.y);
      const nextCardRow = nextCard?.closest('.list-row');
      let cardId = nextCardRow?.dataset?.cardId;
      let listId = nextListId;

      if (!nextCardRow) {
        cardId = this.findCardWithOffset(predictedX, center.y);

        if (!cardId) {
          if (nextListId === 'draft-list-create') return { cardId: null, listId: null };
          const { cardId: guestNextCardId, listId: guestNextListId } = this.handleEmptyListCase(
            nextListId,
            nextListIndex,
            right,
            boardViewNode,
            boardColumnPadding3x,
            altKey
          );
          if (guestNextCardId) {
            cardId = guestNextCardId;
            listId = guestNextListId;
          }
        }
      }
      return { cardId, listId };
    },

    handleHorizontalNextCardSelection(
      nextCardId,
      nextListId,
      altKey,
      cardIdToLookFor,
      selectedCardId
    ) {
      if (nextCardId) {
        if (altKey) {
          this.handleHorizontalAltKeyCardMove(
            nextCardId,
            nextListId,
            cardIdToLookFor,
            selectedCardId
          );
        } else {
          this.setLastHoveredAndSelectedCardId(nextCardId);
        }
        this.$store.dispatch('lastHoveredListId', nextListId);
      } else if (this.inEpicBoard) {
        this.setLastHoveredAndSelectedCardId(cardIdToLookFor);
      }
    },
    handleLastListHorizontalNavigation(right, listIndex, nextCardId, nextListId) {
      if (right && listIndex === this.listIds.length - 1 && this.groupByStatus && !this.isSprint) {
        if (this.isGuestReader) return null;
        this.setLastHoveredAndSelectedCardId(newListButton);
        return {
          nextCardId: newListButton,
          nextListId: 'draft-list-create',
        };
      }
      return { nextCardId, nextListId };
    },

    handleHorizontalInEpicBoard(cardIdToLookFor) {
      if (this.inEpicBoard) {
        const { lastHoveredListId, selectedCardId } = this.$store.getters;
        const currentListCards = this.getListCardsIds(lastHoveredListId);
        if (currentListCards?.length === 0) {
          return this.handleEmptyListInEpicBoard(currentListCards);
        }
        // if cardidtolookfor is not the same as selectedCardId, find list in which card is
        // or if card is not in the current list
        if (cardIdToLookFor !== selectedCardId || !currentListCards.includes(selectedCardId)) {
          for (let i = 0; i < this.listIds.length; i += 1) {
            const listId = this.listIds[i];
            const listCards = this.getListCardsIds(listId);
            if (listCards.includes(cardIdToLookFor)) {
              this.$store.dispatch('lastHoveredListId', listId);
              this.setLastHoveredAndSelectedCardId(cardIdToLookFor);
              this.scrollToCard(cardIdToLookFor, listId);
              return true;
            }
          }
        }
      }
      return false;
    },

    horizontalKeyHandler(key, altKey = false) {
      if (this.isListOrTimelineLayout) return;
      const right = key === 'ArrowRight';
      this.defaultHoveredCardValue();
      const { lastHoveredListId, selectedCardId } = this.$store.getters;
      // check is new list selected
      const listIndex = this.listIds.indexOf(lastHoveredListId);
      const nextListIndex = right ? listIndex + 1 : listIndex - 1;
      let nextListId = this.listIds[nextListIndex];
      const cardIdToLookFor = this.getCardIdToLookFor();
      if (this.handleHorizontalInEpicBoard(cardIdToLookFor)) return;

      this.moveTolastPosition = false;
      let nextCardId;
      if (right && selectedCardId.includes(newListButton)) return;
      if (!right && selectedCardId.includes(newListButton)) {
        // if new list selected and left arrow pressed
        const listCardIds = this.getListCardsIds(lastHoveredListId);
        nextCardId =
          listCardIds.length > 0 ? listCardIds[0] : `${addCardButton}${lastHoveredListId}`;
        this.setLastHoveredAndSelectedCardId(nextCardId);
        return;
      }
      const result = this.handleLastListHorizontalNavigation(
        right,
        listIndex,
        nextCardId,
        nextListId
      );

      if (!result) return;
      nextCardId = result.nextCardId;
      nextListId = result.nextListId;

      if (!cardIdToLookFor) {
        nextCardId = `${addCardButton}${lastHoveredListId}`;
        nextListId = lastHoveredListId;
      }

      if (nextListId !== undefined && nextListId !== null && cardIdToLookFor) {
        const { cardId, listId } = this.findNextHorizontalCardInList(
          nextListId,
          cardIdToLookFor,
          right,
          lastHoveredListId,
          nextListIndex,
          altKey
        );
        if (!cardId) return;
        nextCardId = cardId;
        nextListId = listId;
      }
      this.handleHorizontalNextCardSelection(
        nextCardId,
        nextListId,
        altKey,
        cardIdToLookFor,
        selectedCardId
      );
    },
    arrowLeftHandler(e) {
      return this.horizontalKeyHandler(e.code);
    },
    altArrowLeftHandler(e) {
      this.horizontalKeyHandler(e.code, true);
    },
    altArrowRightHandler(e) {
      this.horizontalKeyHandler(e.code, true);
    },
    enterHandler() {
      const { lastHoveredCardId, selectedCardId, lastHoveredListId } = this.$store.getters;
      const cardId = selectedCardId || lastHoveredCardId;
      if (!cardId) return;
      if (cardId === newListButton) {
        const listId = lastHoveredListId;
        EventBus.$emit('addListButton:click', listId);
        return;
      }
      if (cardId.includes(addCardButton)) {
        const listId = lastHoveredListId;
        EventBus.$emit('addCardButton:click', listId);
        return;
      }
      EventBus.$emit('card:open', cardId);
    },

    keyRHandler() {
      const { lastHoveredCardId, selectedCardId, lastHoveredListId } = this.$store.getters;
      const cardId = selectedCardId || lastHoveredCardId;
      const listId = lastHoveredListId;
      if (!cardId) return;
      if (cardId.includes(addCardButton)) return;
      EventBus.$emit('card:rename', { id: cardId, listId, hideEditKeyboard: true });
    },

    handleKeyEvent(eventName) {
      const { lastHoveredCardId, selectedCardId, lastHoveredListId } = this.$store.getters;
      const cardId = selectedCardId || lastHoveredCardId;
      const listId = lastHoveredListId;
      if (!cardId) return;
      if (cardId.includes(addCardButton)) return;
      EventBus.$emit(`card:${eventName}`, { cardId, listId });
    },

    keyAHandler() {
      this.handleKeyEvent('openAssignees');
    },

    keyDHandler() {
      this.handleKeyEvent('openDueDate');
    },

    keyTHandler() {
      const { lastHoveredCardId, selectedCardId, lastHoveredListId } = this.$store.getters;
      const cardId = selectedCardId || lastHoveredCardId;
      const listId = lastHoveredListId;
      if (!cardId) return;
      if (cardId.includes(addCardButton)) return;
      EventBus.$emit('card:openTags', { cardId, listId });
    },

    keyIHandler(e) {
      if (e.repeat) return;
      this.handleKeyEvent('toggleMyself');
    },

    keySHandler() {
      if (this.isGuestReader) return;
      this.handleKeyEvent('openStatus');
    },

    moveCardToList(nextListIndex, newIndex) {
      const { lastHoveredCardId, selectedCardId, lastHoveredListId } = this.$store.getters;
      const { lastCardIndex } = this.getLastCardIndex();
      const nextListId = this.listIds[nextListIndex];
      if (nextListId) {
        const cardIdToLookFor = lastHoveredCardId || selectedCardId;
        const cardType =
          this.board.id === this.$constants.epicsBoardId ? ResourceType.Epic : ResourceType.Card;
        EventBus.$emit('card:add', {
          listId: nextListId,
          newIndex,
          oldIndex: lastCardIndex,
          cardId: cardIdToLookFor,
          boardId: this.board.id,
          projectId: this.board.project_id,
          originalListId: lastHoveredListId,
          type: cardType,
        });
        this.$nextTick(() => {
          const selectorCard = `.list-row[data-card-id="${cardIdToLookFor}"][data-list-id="${lastHoveredListId}"]`;
          this.isInViewportAndScrollVertical(selectorCard);
        });
        this.$store.dispatch('lastHoveredListId', nextListId);
        this.setLastHoveredAndSelectedCardId(cardIdToLookFor);
      }
    },
    periodHandler() {
      const { lastHoveredCardId, selectedCardId } = this.$store.getters;
      const cardId = selectedCardId || lastHoveredCardId;
      if (!cardId) return;
      if (cardId.includes(addCardButton)) return;
      this.copyGitBranchNameShortcutHandler(cardId);
    },
    altArrowUpDownHandler(e) {
      const down = e.key === 'ArrowDown';
      const { lastHoveredCardId, selectedCardId, lastHoveredListId } = this.$store.getters;
      const { listCardIds, lastCardIndex } = this.getLastCardIndex();
      const nextIndex = down ? lastCardIndex + 1 : lastCardIndex - 1;
      // move only within the scope of the list - grid layout
      if (!this.isListOrTimelineLayout && (nextIndex < 0 || nextIndex > listCardIds.length - 1))
        return;
      // not move if card new button is selected
      if (selectedCardId && selectedCardId.includes(addCardButton)) return;

      if (this.isListOrTimelineLayout) {
        const listIndex = this.listIds.indexOf(lastHoveredListId);
        if (lastCardIndex === 0 && !down) {
          // move card to list above
          // check if first list
          if (listIndex === 0) return;
          const nextListIndex = listIndex - 1;
          const nextListId = this.listIds[nextListIndex];
          this.moveCardToList(nextListIndex, this.getListCardsIds(nextListId).length);
          return;
        } else if (lastCardIndex === listCardIds.length - 1 && down) {
          // move card to list below
          // check if list is the last one
          if (listIndex === this.listIds.length - 1) return;
          const nextListIndex = listIndex + 1;
          this.moveCardToList(nextListIndex, 0);
          return;
        }
      }

      const cardId = selectedCardId || lastHoveredCardId;
      const cardType =
        this.board.id === this.$constants.epicsBoardId ? ResourceType.Epic : ResourceType.Card;
      const payload = {
        listId: lastHoveredListId,
        newIndex: nextIndex,
        oldIndex: lastCardIndex,
        cardId,
        boardId: this.board.id,
        type: cardType,
      };
      EventBus.$emit('card:move', payload);
      this.$store.dispatch('setSelectedCardId', cardId);
      this.$nextTick(() => {
        if (this.isListOrTimelineLayout) {
          const selectorCard = `.list-row[data-card-id="${cardId}"][data-list-id="${lastHoveredListId}"]`;
          this.isInViewportAndScrollVertical(selectorCard);
          return;
        }
        EventBus.$emit('card:checkIsInViewport', cardId);
      });
    },
    checkIsListInViewport(listId) {
      const listElement = this.$refs.boardView.$el.querySelector(
        `.board-column[data-list-id="${listId}"]`
      );
      if (!listElement) return { horizontal: false, deltaH: 0 };
      const { horizontal, deltaH } = isInViewport(listElement, this.$refs.boardView);
      return { horizontal, deltaH };
    },
    listViewportMessageHandler(listId) {
      const { horizontal, deltaH } = this.checkIsListInViewport(listId);
      if (!horizontal && deltaH) {
        const absDelta = Math.abs(deltaH);
        const dx = deltaH > 0 ? absDelta : -absDelta;
        this.$refs.boardView.scrollBy({ dx }, 0);
      }
    },
    checkListCompletedSprint(listBehavior) {
      return (
        listBehavior === ListBehavior.Completed ||
        listBehavior === ListBehavior.Cancelled ||
        listBehavior === this.$constants.rolledOverList
      );
    },
  },
};
</script>
